/** 샵관리 — 고객등록. 이용중 회원권·잔여횟수·최근방문은 회원권/시술 데이터에서 자동 산출(읽기 전용) */
import React from 'react';
import { hiCol } from 'components/grid/hiGrid';
import ShopGridPage from './ShopGridPage';
import {
  loadTable, persistTable, createUid, nextCode, digitsOnly, formatPhone, today, customerSummaries,
} from 'shop/shopStore';
import { kwField, ynField, matchKeyword, dupErrors } from 'shop/shopPageHelpers';

const DERIVED = ['passSummary', 'remainCnt', 'lastVisit'];

const filter = (rows, v) =>
  rows.filter(
    (r) => r.mod !== 'D'
      && (!v.useYn || r.useYn === v.useYn)
      && matchKeyword(
        { ...r, phoneDigits: digitsOnly(r.phone) },
        ['customerNo', 'customerNm', 'phone', 'phoneDigits', 'remark'],
        v.keyword
      )
  );

const cfg = {
  gridId: 'sh-customer',
  title: '고객등록',
  uidKey: 'customerUid',
  searchDefaults: { keyword: '', useYn: 'Y' },
  searchFields: () => [[kwField('이름·번호·핸드폰·비고'), ynField()]],
  columns: () => [
    hiCol.edTextColumnForKey({ header: '고유번호', field: 'customerNo', size: 100, required: true }),
    hiCol.edTextColumn({ header: '이름', field: 'customerNm', size: 110, required: true }),
    hiCol.edTextColumn({ header: '핸드폰', field: 'phone', size: 130 }),
    hiCol.edDateColumn({ header: '첫방문일', field: 'firstVisitDt', size: 120 }),
    hiCol.roTextColumn({ header: '이용중 회원권 (잔여/전체)', field: 'passSummary', size: 260 }),
    hiCol.roNumberColumn({ header: '잔여횟수', field: 'remainCnt', size: 90 }),
    hiCol.roTextColumn({ header: '최근방문', field: 'lastVisit', size: 110, align: 'center' }),
    hiCol.edTextColumn({ header: '비고', field: 'remark', size: 240 }),
    hiCol.edCheckboxColumn({ header: '사용', field: 'useYn', size: 72 }),
  ],
  load: (tc) => {
    const summary = customerSummaries(tc);
    return loadTable(tc, 'customer').map((c) => ({ ...c, mod: '', ...summary(c.customerUid) }));
  },
  filter,
  emptyRow: (rows) => ({
    customerUid: createUid('CUS'),
    customerNo: nextCode(rows, 'customerNo', 'C', 4),
    customerNm: '',
    phone: '',
    firstVisitDt: today(),
    remark: '',
    useYn: 'Y',
  }),
  validate: (rows, tc) => {
    const errors = dupErrors(rows, 'customerNo', '고유번호');
    const used = new Set([
      ...loadTable(tc, 'pass').map((p) => p.customerUid),
      ...loadTable(tc, 'record').map((r) => r.customerUid),
    ]);
    for (const r of rows) {
      if (r.mod === 'D') {
        if (used.has(r.customerUid)) errors.push(`회원권·시술 이력이 있는 고객은 삭제할 수 없습니다(사용=N 처리): ${r.customerNm}`);
        continue;
      }
      if (!String(r.customerNo || '').trim()) errors.push('고유번호는 필수입니다.');
      if (!String(r.customerNm || '').trim()) errors.push(`이름 누락 (${r.customerNo})`);
      const d = digitsOnly(r.phone);
      if (d && (d.length < 9 || d.length > 11)) errors.push(`핸드폰 번호 자릿수 확인: ${r.customerNm}`);
    }
    return errors;
  },
  save: (tc, rows, v) => {
    // 같은 번호 고객 — 경고 후 확인하면 저장 (동명이인·가족 공용번호 허용)
    const others = loadTable(tc, 'customer');
    const dups = [];
    for (const r of rows) {
      const d = digitsOnly(r.phone);
      if (r.mod === 'D' || !d) continue;
      const hit = [...others.filter((o) => o.customerUid !== r.customerUid), ...rows.filter((o) => o !== r && o.mod !== 'D')]
        .find((o) => digitsOnly(o.phone) === d);
      if (hit) dups.push(`${r.customerNm} ↔ ${hit.customerNm} (${formatPhone(d)})`);
    }
    if (dups.length && !window.confirm(`같은 핸드폰 번호의 고객이 있습니다.\n${[...new Set(dups)].slice(0, 5).join('\n')}\n\n그래도 저장할까요?`)) {
      throw new Error('저장을 취소했습니다.');
    }
    persistTable(tc, 'customer', 'customerUid', rows, (r) => filter([r], v).length > 0, (r) => {
      const next = { ...r, phone: digitsOnly(r.phone), customerNm: String(r.customerNm).trim(), useYn: r.useYn === 'N' ? 'N' : 'Y' };
      DERIVED.forEach((k) => delete next[k]);
      return next;
    });
  },
};

const CustomerRegister = () => <ShopGridPage cfg={cfg} />;
export default CustomerRegister;
